import React from "react";
import { Check, X } from "lucide-react";
import { Checkbox } from "@/global/base/checkbox.tsx";
import { RippleHoverButton } from "@/components/global/RippleHoverButton/RippleHoverButton";

function RegistrationActiveCell({ valid }) {
  if (valid) {
    return (
      <span className="inline-flex items-center gap-2 text-sm text-emerald-700">
        <span
          className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white"
          aria-hidden
        >
          <Check className="h-3 w-3" strokeWidth={2.5} aria-hidden />
        </span>
        Valid Registration
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 text-sm text-red-600">
      <span
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-600 text-white"
        aria-hidden
      >
        <X className="h-3 w-3" strokeWidth={2.5} aria-hidden />
      </span>
      Invalid Registration
    </span>
  );
}

function YesNoActiveCell({ active }) {
  if (active) {
    return (
      <span className="inline-flex items-center gap-2 text-sm text-emerald-700">
        <span
          className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white"
          aria-hidden
        >
          <Check className="h-3 w-3" strokeWidth={2.5} aria-hidden />
        </span>
        Yes
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 text-sm text-red-600">
      <span
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-600 text-white"
        aria-hidden
      >
        <X className="h-3 w-3" strokeWidth={2.5} aria-hidden />
      </span>
      No
    </span>
  );
}

/**
 * @param {Object} options
 * @param {(row: object) => void} options.onDeactivate
 * @param {(row: object) => void} options.onActivate
 */
export function getUserManagementColumns({
  onDeactivate,
  onActivate,
  actionUserId,
  canChangeUser = false,
}) {
  const columns = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          data-testid="user-management-select-all"
        />
      ),
      cell: ({ row }) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label={`Select row ${row.original?.id}`}
            data-testid={`user-management-select-${row.original?.id}`}
          />
        </div>
      ),
      meta: { disableFilterMenu: true },
      size: 40,
    },
    {
      id: "registration_number",
      accessorKey: "registration_number",
      header: "Registration Number",
      cell: ({ row }) => (
        <span className="text-sm text-slate-800">
          {row.original?.registration_number ?? "—"}
        </span>
      ),
    },
    {
      id: "registration_active",
      accessorKey: "registration_valid",
      header: "Registration Active",
      cell: ({ row }) => (
        <RegistrationActiveCell valid={row.original?.registration_valid} />
      ),
      meta: { disableFilterMenu: true },
    },
    {
      id: "first_name",
      accessorKey: "first_name",
      header: "First Name",
      cell: ({ row }) => (
        <span className="text-sm text-slate-800">{row.original?.first_name ?? "—"}</span>
      ),
    },
    {
      id: "last_name",
      accessorKey: "last_name",
      header: "Last Name",
      cell: ({ row }) => (
        <span className="text-sm text-slate-800">{row.original?.last_name ?? "—"}</span>
      ),
    },
    {
      id: "user_type",
      accessorKey: "user_type",
      header: "User Type",
      cell: ({ row }) => (
        <span className="text-sm text-slate-800">{row.original?.user_type ?? "—"}</span>
      ),
    },
    {
      id: "registered_products",
      accessorKey: "registered_products",
      header: "Registered Products",
      cell: ({ row }) => (
        <span className="text-sm text-slate-800">
          {row.original?.registered_products ?? "—"}
        </span>
      ),
    },
    {
      id: "date_joined",
      accessorKey: "date_joined",
      header: "Date Joined",
      cell: ({ row }) => (
        <span className="text-sm text-slate-800">{row.original?.date_joined ?? "—"}</span>
      ),
    },
    {
      id: "brochure_downloaded",
      accessorKey: "brochure_downloaded",
      header: "Brochure Downloaded",
      cell: ({ row }) => (
        <span className="text-sm text-slate-800">
          {row.original?.brochure_downloaded ?? "—"}
        </span>
      ),
    },
    {
      id: "active_account",
      accessorKey: "active_account",
      header: "Active Account",
      cell: ({ row }) => (
        <YesNoActiveCell active={row.original?.active_account} />
      ),
      meta: { disableFilterMenu: true },
    },
  ];

  if (canChangeUser) {
    columns.push({
      id: "action",
      header: "Action",
      enableSorting: false,
      cell: ({ row }) => {
        const active = row.original?.active_account;
        const isBusy = actionUserId === row.original?.id;
        return (
          <div
            className="flex justify-end gap-2"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="presentation"
          >
            {active ? (
              <RippleHoverButton
                variant="danger"
                className="h-[36px]"
                onClick={() => onDeactivate(row.original)}
                disabled={isBusy}
                data-testid={`user-management-deactivate-${row.original?.id}`}
              >
                {isBusy ? "…" : "Deactivate"}
              </RippleHoverButton>
            ) : (
              <RippleHoverButton
                variant="success"
                className="h-[36px]"
                onClick={() => onActivate(row.original)}
                disabled={isBusy}
                data-testid={`user-management-activate-${row.original?.id}`}
              >
                {isBusy ? "…" : "Activate"}
              </RippleHoverButton>
            )}
          </div>
        );
      },
      meta: { disableFilterMenu: true, align: "center" },
    });
  }

  return columns;
}
